/**
 * User Service Interface
 * 
 * Contract ที่ทุก Backend ต้อง implement
 * UI เรียกผ่าน Interface นี้เท่านั้น
 */

import type { 
  AdminUser, 
  Member, 
  LoginCredentials, 
  LoginResult,
  SessionValidation,
} from './user.types';

// ============================================
// Service Interface
// ============================================

export interface IUserService {
  // Auth
  login(credentials: LoginCredentials): Promise<LoginResult>;
  logout(sessionToken: string, userData?: AdminUser): Promise<void>;
  validateSession(sessionToken: string): Promise<SessionValidation>;
  getCurrentUser(sessionToken: string): Promise<AdminUser | null>;
  changePassword(sessionToken: string, userId: string, newPassword: string): Promise<void>;
  
  // Admin CRUD
  getAdmins(sessionToken: string, params: QueryParams): Promise<PaginatedResult<AdminUser>>;
  getAdmin(sessionToken: string, id: string): Promise<AdminUser>;
  createAdmin(sessionToken: string, data: CreateAdminData): Promise<AdminUser>;
  updateAdmin(sessionToken: string, id: string, data: UpdateAdminData): Promise<AdminUser>;
  deleteAdmin(sessionToken: string, id: string): Promise<void>;
  
  // Member CRUD
  getMembers(sessionToken: string, params: QueryParams): Promise<PaginatedResult<Member>>;
  getMember(sessionToken: string, id: string): Promise<Member>;
  createMember(sessionToken: string, data: CreateMemberData): Promise<Member>;
  updateMember(sessionToken: string, id: string, data: UpdateMemberData): Promise<Member>;
  deleteMember(sessionToken: string, id: string): Promise<void>;
  
  // Member Actions
  activateMember(sessionToken: string, id: string): Promise<void>;
  suspendMember(sessionToken: string, id: string): Promise<void>;
  resetPromotion(sessionToken: string, id: string): Promise<void>;
  clearCredit(sessionToken: string, id: string): Promise<void>;
  resetTurnover(sessionToken: string, id: string, type: 'cash' | 'bonus'): Promise<void>;
}

// ============================================
// Supporting Types
// ============================================

export interface QueryParams {
  page?: number;
  limit?: number;
  search?: string;           // canonical search
  orderBy?: 'asc' | 'desc';
  orderByField?: string;
  filters?: Record<string, any>;
}

export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateAdminData {
  username: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  lineId?: string;
  permissions?: string[];  // Array ของ permission keys
  adminGroup?: number;
}

export interface UpdateAdminData {
  firstName?: string;
  lastName?: string;
  phone?: string;
  lineId?: string;
  email?: string;
  password?: string;
  status?: string;
  permissions?: string[];  // Array ของ permission keys
  adminGroup?: number;
}

export interface CreateMemberData {
  firstName: string;
  lastName: string;
  phone: string;             // mobile_phone = username
  password: string;
  bankName: string;
  bankAccount: string;
  bankType?: number;         // 1=Bank, 2=TrueWallet (auto-calculated from bankName)
  lineId?: string;
  email?: string;
}

export interface UpdateMemberData {
  firstName?: string;
  lastName?: string;
  phone?: string;
  bankName?: string;
  bankAccount?: string;
  bankType?: number;         // 1=Bank, 2=TrueWallet (auto-calculated from bankName)
  lineId?: string;
  email?: string;
  password?: string;
  status?: string;
}

// ============================================
// Factory
// ============================================

import { getBackendType, registerServiceClearCallback, type BackendType } from '@/config/api.config';

let currentService: IUserService | null = null;

// Register clear callback
registerServiceClearCallback(() => {
  currentService = null;
});

/**
 * Get User Service based on current backend configuration
 */
export async function getUserService(): Promise<IUserService> {
  if (currentService) return currentService;
  
  const backend = getBackendType();
  
  switch (backend) {
    case 'parse-v1':
      const { ParseV1UserService } = await import('./adapters/parse-v1.adapter');
      currentService = new ParseV1UserService();
      break;
      
    case 'parse-v2':
      const { ParseV2UserService } = await import('./adapters/parse-v2.adapter');
      currentService = new ParseV2UserService();
      break;
      
    case 'go':
      // TODO: Implement Go adapter
      throw new Error('Go backend not implemented yet. Coming soon!');
      
    case 'mock':
      // TODO: Implement Mock adapter
      throw new Error('Mock backend not implemented yet');
      
    default:
      throw new Error(`Unknown backend: ${backend}`);
  }
  
  console.log(`📦 [UserService] Using backend: ${backend}`);
  return currentService;
}

// Re-export for convenience
export type { BackendType };

